import { useAudioTranscription } from "@/api/audio-transcription";
import { AudioDeviceSetupModal } from "@/components/AudioDeviceSetupModal";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAudioRecording } from "@/hooks/useAudioRecording";
import { cn } from "@/utils";
import * as analytics from "@/utils/analytics";
import { AudioDevice, audioSettingsStorage } from "@/utils/audioSettings";
import { Spinner } from "@cohesive/ui-components";
import createDebug from "debug";
import { ArrowUp, Loader2, Mic, MicOff, Square } from "lucide-react";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import toast from "react-hot-toast";
import { AudioWaveform } from "./AudioWaveform";

const debug = createDebug("cohesive:audio-recording");

interface AudioRecordingButtonProps {
  onTranscriptionComplete?: (text: string) => void;
  /** When provided, a send button appears during recording. Clicking it stops
   * recording and passes the transcribed text directly to this callback,
   * bypassing the editor. */
  onSendImmediately?: (text: string) => void;
  disabled?: boolean;
  /** Mic / stop / error states — same as `MessageInput` `audioRecordingButton`. */
  className?: string;
  /** Send-while-recording control — same classes as `SendButton` `sendButtonClassName`. */
  sendButtonClassName?: string;
  /** Arrow icon on send-while-recording — same as `SendButton` `iconClassName`. */
  sendIconClassName?: string;
  micIconClassName?: string;
  onRecordingStateChange?: (isRecording: boolean) => void;
}

export interface AudioRecordingButtonRef {
  startRecording: () => Promise<void>;
  stopRecording: () => void;
}

export const AudioRecordingButton = forwardRef<
  AudioRecordingButtonRef,
  AudioRecordingButtonProps
>(
  (
    {
      onTranscriptionComplete,
      onSendImmediately,
      disabled = false,
      className,
      sendButtonClassName,
      sendIconClassName,
      micIconClassName,
      onRecordingStateChange,
    },
    ref,
  ) => {
    const [showDeviceSetup, setShowDeviceSetup] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);

    // Timeout ref for transcription safety net
    const transcriptionTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // When true, the next transcription result is sent immediately via onSendImmediately
    const sendImmediatelyRef = useRef(false);
    const onSendImmediatelyRef = useRef(onSendImmediately);
    onSendImmediatelyRef.current = onSendImmediately;

    /** When true, recording was cancelled (Escape); skip transcription for this blob. */
    const recordCancelledRef = useRef(false);

    // Reset states helper function
    const resetTranscriptionStates = useCallback(() => {
      setIsUploading(false);
      setIsTranscribing(false);
      if (transcriptionTimeoutRef.current) {
        clearTimeout(transcriptionTimeoutRef.current);
        transcriptionTimeoutRef.current = null;
      }
    }, []);

    const transcriptionMutation = useAudioTranscription({
      onSuccess: (data) => {
        resetTranscriptionStates();
        if (data.text) {
          if (sendImmediatelyRef.current && onSendImmediatelyRef.current) {
            sendImmediatelyRef.current = false;
            onSendImmediatelyRef.current(data.text);
          } else {
            sendImmediatelyRef.current = false;
            onTranscriptionComplete?.(data.text);
          }
        } else {
          sendImmediatelyRef.current = false;
          toast.error("No transcription text received");
        }
      },
      onError: (error) => {
        sendImmediatelyRef.current = false;
        resetTranscriptionStates();
        debug("Transcription error:", error);
        toast.error(
          error instanceof Error
            ? `Transcription failed: ${error.message}`
            : "Failed to transcribe audio",
        );
      },
    });

    const handleRecordingComplete = useCallback(
      async (audioBlob: Blob) => {
        debug("🎤 handleRecordingComplete called", {
          blobSize: audioBlob.size,
          hasTranscriptionCallback: !!onTranscriptionComplete,
        });

        if (recordCancelledRef.current) {
          recordCancelledRef.current = false;
          return;
        }

        if (!onTranscriptionComplete && !onSendImmediately) return;

        try {
          // Start uploading state
          debug("📤 Starting upload state...");
          setIsUploading(true);

          // Small delay to show uploading state, then switch to transcribing
          setTimeout(() => {
            debug("📝 Switching to transcribing state...");
            setIsUploading(false);
            setIsTranscribing(true);
          }, 500);

          debug("🚀 Starting transcription mutation...");

          // Set a safety timeout (30 seconds) to reset states if transcription hangs
          transcriptionTimeoutRef.current = setTimeout(() => {
            debug("⚠️ Transcription timeout reached, resetting states");
            resetTranscriptionStates();
            toast.error("Transcription timed out. Please try again.");
          }, 30000);

          transcriptionMutation.mutate({
            audioBlob,
            options: {
              model: "gpt-4o-transcribe",
              response_format: "json",
            },
          });
        } catch (error) {
          // If anything fails in the setup, reset states
          debug("Error in handleRecordingComplete:", error);
          resetTranscriptionStates();
          toast.error("Failed to start transcription process");
        }
      },
      [
        onTranscriptionComplete,
        onSendImmediately,
        transcriptionMutation,
        resetTranscriptionStates,
      ],
    );

    const handleError = useCallback(
      (error: Error) => {
        // Reset all states on error
        resetTranscriptionStates();
        debug("Recording error:", error);
        toast.error(`Recording failed: ${error.message}`);
      },
      [resetTranscriptionStates],
    );

    const handleMaxDurationReached = useCallback(() => {
      analytics.trackAudioRecordingStopped({
        durationSeconds: 120, // Max duration is always 2 minutes (120 seconds)
        wasAutoStopped: true,
      });
      toast(
        "Recording stopped automatically after reaching 2-minute limit. Transcribing audio...",
        {
          icon: "⏱️",
        },
      );
    }, []);

    const {
      isRecording,
      isSupported,
      audioLevel,
      error,
      startRecording,
      stopRecording,
      duration,
    } = useAudioRecording({
      onRecordingComplete: handleRecordingComplete,
      onError: handleError,
      onMaxDurationReached: handleMaxDurationReached,
    });

    const handleSendWhileRecording = useCallback(() => {
      recordCancelledRef.current = false;
      sendImmediatelyRef.current = true;
      if (isRecording) {
        analytics.trackAudioRecordingStopped({
          durationSeconds: duration,
          wasAutoStopped: false,
        });
        stopRecording();
      }
    }, [isRecording, stopRecording, duration]);

    const handleToggleRecording = useCallback(async () => {
      if (isRecording) {
        recordCancelledRef.current = false;
        analytics.trackAudioRecordingStopped({
          durationSeconds: duration,
          wasAutoStopped: false,
        });
        stopRecording();
      } else {
        // Check if a device is configured before starting recording
        if (!audioSettingsStorage.hasConfiguredDevice()) {
          setShowDeviceSetup(true);
          return;
        }
        recordCancelledRef.current = false;
        analytics.trackAudioRecordingStarted();
        await startRecording();
      }
    }, [isRecording, startRecording, stopRecording, duration]);

    const handleDeviceSelected = useCallback(
      (device: AudioDevice) => {
        toast.success(`Microphone "${device.label}" configured successfully!`);
        setShowDeviceSetup(false);

        // Automatically start recording after device is configured
        setTimeout(() => {
          recordCancelledRef.current = false;
          analytics.trackAudioRecordingStarted();
          startRecording();
        }, 100);
      },
      [startRecording],
    );

    const handleDeviceSetupCancel = useCallback(() => {
      setShowDeviceSetup(false);
    }, []);

    // Expose startRecording and stopRecording functions through ref
    useImperativeHandle(
      ref,
      () => ({
        startRecording: async () => {
          if (!audioSettingsStorage.hasConfiguredDevice()) {
            setShowDeviceSetup(true);
            return;
          }
          recordCancelledRef.current = false;
          analytics.trackAudioRecordingStarted();
          await startRecording();
        },
        stopRecording: () => {
          if (isRecording) {
            analytics.trackAudioRecordingStopped({
              durationSeconds: duration,
              wasAutoStopped: false,
            });
          }
          stopRecording();
        },
      }),
      [startRecording, stopRecording, isRecording, duration],
    );

    // Notify parent component of recording state changes
    useEffect(() => {
      onRecordingStateChange?.(isRecording);
    }, [isRecording, onRecordingStateChange]);

    // Capture-phase shortcuts while recording (match canvas chat input)
    useEffect(() => {
      if (!isRecording) return;
      const handler = (e: KeyboardEvent) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          e.stopPropagation();
          recordCancelledRef.current = false;
          analytics.trackAudioRecordingStopped({
            durationSeconds: duration,
            wasAutoStopped: false,
          });
          stopRecording();
        }
        if (e.key === "Escape") {
          e.preventDefault();
          e.stopPropagation();
          recordCancelledRef.current = true;
          analytics.trackAudioRecordingStopped({
            durationSeconds: duration,
            wasAutoStopped: false,
          });
          stopRecording();
        }
      };
      document.addEventListener("keydown", handler, true);
      return () => document.removeEventListener("keydown", handler, true);
    }, [isRecording, stopRecording, duration]);

    useEffect(() => {
      return () => {
        recordCancelledRef.current = true;
      };
    }, []);

    // Cleanup states on unmount and clear any pending timeouts
    useEffect(() => {
      return () => {
        resetTranscriptionStates();
      };
    }, [resetTranscriptionStates]);

    if (!isSupported) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-8 h-8 rounded-full text-neutral-400 cursor-not-allowed",
                className,
              )}
              disabled
            >
              <MicOff className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Audio recording not supported in this browser
          </TooltipContent>
        </Tooltip>
      );
    }

    if (error) {
      return (
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                "w-8 h-8 rounded-full text-lc-blocked hover:text-lc-blocked dark:hover:text-lc-blocked transition-colors",
                className,
              )}
              onClick={handleToggleRecording}
              disabled={disabled}
            >
              <MicOff className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="bottom">
            Recording error: {error}
          </TooltipContent>
        </Tooltip>
      );
    }

    return (
      <>
        <div className="flex items-center">
          {/* Recording / upload / transcribe row (shared layout; send-while-recording only if onSendImmediately) */}
          {(isRecording || isUploading || isTranscribing) &&
          (onSendImmediately || onTranscriptionComplete) ? (
            <>
              {/* Left: stop button — always enabled so users can transcribe even
                  while the session is processing */}
              {isRecording && (
                <Button
                  variant="ghost"
                  className={cn(
                    "w-8 h-8 rounded-full py-1 px-1 transition-colors cursor-pointer hover:bg-transparent",
                    "text-lc-blocked hover:text-lc-blocked dark:hover:text-lc-blocked",
                    className,
                  )}
                  onClick={handleToggleRecording}
                >
                  <Square className="h-3 w-3 fill-current" />
                </Button>
              )}

              {/* Center: waveform+timer while recording; upload / transcribe while processing */}
              <div className="flex items-center gap-1.5 mx-1 mr-3 flex-1 min-w-0 justify-center">
                {isRecording ? (
                  <>
                    <span
                      className={cn(
                        "text-xs font-mono tabular-nums transition-colors shrink-0",
                        duration >= 90
                          ? "text-lc-blocked dark:text-lc-blocked font-semibold"
                          : "text-neutral-500 dark:text-neutral-400",
                      )}
                    >
                      {String(Math.floor(duration / 60)).padStart(2, "0")}:
                      {String(duration % 60).padStart(2, "0")}
                      {duration >= 90 && (
                        <span className="ml-1 text-[10px]">
                          ({120 - duration}s left)
                        </span>
                      )}
                    </span>
                    <AudioWaveform
                      audioLevel={audioLevel}
                      isRecording={isRecording}
                    />
                  </>
                ) : isUploading ? (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Spinner size="sm" />
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-foreground">
                    <Spinner size="sm" />
                  </div>
                )}
              </div>

              {/* Right: send button — only when immediate send is supported */}
              {isRecording && onSendImmediately && (
                <Button
                  onClick={handleSendWhileRecording}
                  variant={disabled ? "outline" : "ghost"}
                  className={cn(
                    "rounded-full w-9 h-9 transition-all duration-200 disabled:opacity-100",
                    disabled
                      ? "cursor-not-allowed border-input bg-transparent text-muted-foreground hover:bg-transparent dark:hover:bg-transparent"
                      : "bg-primary hover:bg-primary/90 text-primary-foreground border-transparent hover:border-transparent",
                    sendButtonClassName,
                  )}
                  disabled={disabled}
                  size="icon"
                >
                  <ArrowUp
                    className={cn(
                      "h-5! w-5!",
                      sendIconClassName,
                      "transition-colors",
                      disabled
                        ? "text-muted-foreground"
                        : "text-primary-foreground",
                    )}
                  />
                </Button>
              )}
            </>
          ) : (
            <>
              {/* Legacy layout: waveform+timer left, mic/stop button right */}
              {(isRecording || isUploading || isTranscribing) && (
                <div className="flex items-center space-x-2 mr-2">
                  {isUploading ? (
                    <div className="flex items-center space-x-1">
                      <div className="w-3 h-3 border border-teal/40 border-t-transparent rounded-full animate-spin" />
                      <span className="text-xs text-teal dark:text-teal">
                        Uploading...
                      </span>
                    </div>
                  ) : isTranscribing ? (
                    <div className="flex items-center gap-2 text-sm text-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />
                      <span>Transcribing…</span>
                    </div>
                  ) : (
                    <div className="flex items-center space-x-1">
                      <AudioWaveform
                        audioLevel={audioLevel}
                        isRecording={isRecording}
                      />
                      <span
                        className={cn(
                          "text-xs font-mono tabular-nums transition-colors",
                          duration >= 90
                            ? "text-lc-blocked dark:text-lc-blocked font-semibold"
                            : "text-neutral-500 dark:text-neutral-400",
                        )}
                      >
                        {String(Math.floor(duration / 60)).padStart(2, "0")}:
                        {String(duration % 60).padStart(2, "0")}
                        {duration >= 90 && (
                          <span className="ml-1 text-[10px]">
                            ({120 - duration}s left)
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Recording button */}
              <Tooltip delayDuration={0}>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    className={cn(
                      "w-8 h-8 rounded-full py-1 px-1 transition-colors cursor-pointer hover:bg-accent",
                      isRecording
                        ? "text-lc-blocked hover:text-lc-blocked dark:hover:text-lc-blocked"
                        : // Theme-aware hover (matches every other toolbar
                          // button) — `hover:text-white` turned the mic invisible
                          // on the light-mode input.
                          "text-muted-foreground hover:text-foreground",
                      className,
                    )}
                    onClick={handleToggleRecording}
                    disabled={disabled || isUploading || isTranscribing}
                  >
                    {isRecording ? (
                      <Square className="h-3 w-3 fill-current" />
                    ) : (
                      <Mic className={cn("h-5! w-5!", micIconClassName)} />
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Dictate</TooltipContent>
              </Tooltip>
            </>
          )}
        </div>

        {/* Audio Device Setup Modal */}
        <AudioDeviceSetupModal
          isOpen={showDeviceSetup}
          onClose={handleDeviceSetupCancel}
          onDeviceSelected={handleDeviceSelected}
          title="Set up your microphone"
          description="To start recording, please select and test your microphone. This setup only needs to be done once."
        />
      </>
    );
  },
);

AudioRecordingButton.displayName = "AudioRecordingButton";
